import { useState, useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setAlert } from "../../../actions/alert";
import { addVehicle, getVehicle } from "../../../actions/admin/vehicle";
import { getAllVehicleTypes } from "../../../actions/admin/vehicletype";
import Alert from "../../layout/Alert";
import Spinner from "../../layout/Spinner";
import AdminPage from "../_components/AdminPage";
import {
  AdminFormCard,
  AdminTextField,
  AdminSelectField,
  AdminToggleField,
  AdminBackLink,
  AdminFormButton,
  AdminFormActions,
} from "../_components/AdminForm";
import ConfirmationModal from "../components/ConfirmationModal";
import SuccessModal from "../components/SuccessModal";
import { ChangeList, diffFields } from "../components/ChangeSummary";
import useSaveConfirmation from "../components/useSaveConfirmation";
import { VEHICLE_FIELDS } from "./AddVehicle";

const VEHICLE_LIST = "/admin/vehiclelist";

const UpdateVehicle = ({ match }) => {
  const dispatch = useDispatch();
  const { loading, vehicle } = useSelector((state) => state.vehicle);

  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
  const [formData, setFormData] = useState({
    _id: "",
    vehiclename: "",
    vehicletype: "",
    vehicletypeid: "",
    registration: "",
    isactive: false,
  });
  const { vehiclename, vehicletype, registration, isactive } = formData;

  // The stored record as the form saw it when it loaded — the "before" side of the
  // change summary.
  const savedFormData = useRef(null);

  useEffect(() => {
    dispatch(getVehicle(match.params.id));
  }, [dispatch, match.params.id]);

  useEffect(() => {
    let cancelled = false;

    // Without this guard the response from a previous vehicle's screen could land
    // after this one mounted and overwrite its options.
    getAllVehicleTypes().then((types) => {
      if (cancelled) return;
      setVehicleTypeOptions(types.map((row) => ({ value: row._id, label: row.typename })));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const option = vehicleTypeOptions.find((op) => op.label === vehicle?.vehicletype);

    const loadedFormData = {
      _id: loading || !vehicle?._id ? "" : vehicle._id,
      vehiclename: loading || !vehicle?.vehiclename ? "" : vehicle.vehiclename,
      vehicletype: option?.label || "",
      // Fall back to the stored id while the type options are still loading.
      vehicletypeid: option?.value ?? vehicle?.vehicletypeid ?? "",
      registration: loading || !vehicle?.registration ? "" : vehicle.registration,
      isactive: loading || !vehicle?.isactive ? false : vehicle.isactive,
    };

    savedFormData.current = loadedFormData;
    setFormData(loadedFormData);
  },[vehicleTypeOptions, loading, vehicle]);

  const save = useSaveConfirmation({
    save: () => dispatch(addVehicle(formData, null, true)),
  });

  const onSubmit = (e) => {
    e.preventDefault();

    const changes = diffFields(VEHICLE_FIELDS, savedFormData.current || {}, formData);

    if (changes.length === 0) {
      dispatch(setAlert("Nothing to update -- this vehicle is unchanged.", "primary"));
      return;
    }

    save.request(changes);
  };

  const actions = <AdminBackLink to={VEHICLE_LIST} />;

  if (loading) {
    return (
      <AdminPage title="Update Vehicle" actions={actions}>
        <Spinner />
      </AdminPage>
    );
  }

  return (
    <AdminPage title="Update Vehicle" actions={actions}>
      <form onSubmit={onSubmit} className="tw:max-w-[720px]">
        <AdminFormCard title="Vehicle status">
          <AdminToggleField
            name="isactive"
            label="Vehicle is active"
            description="Only active vehicles can be assigned to a booking."
            checked={isactive}
            onChange={() => setFormData({ ...formData, isactive: !isactive })}
          />
        </AdminFormCard>

        <AdminFormCard title="Vehicle details" columns={2}>
          <AdminTextField
            name="vehiclename"
            label="Vehicle name"
            placeholder="Enter name"
            value={vehiclename}
            onChange={(e) => setFormData({ ...formData, vehiclename: e.target.value })}
            required
          />

          <AdminSelectField
            name="vehicletype"
            label="Vehicle type"
            placeholder="Select a vehicle type"
            options={vehicleTypeOptions}
            value={vehicleTypeOptions.find((o) => o.label === vehicletype) || null}
            onChange={(option) =>
              setFormData({ ...formData, vehicletype: option.label, vehicletypeid: option.value })
            }
          />

          <AdminTextField
            name="registration"
            label="Vehicle registration"
            placeholder="Enter vehicle registration"
            value={registration}
            onChange={(e) => setFormData({ ...formData, registration: e.target.value })}
            required
          />
        </AdminFormCard>

        <AdminFormActions>
          <AdminFormButton type="submit">Update</AdminFormButton>
        </AdminFormActions>
      </form>

      <Alert />

      <ConfirmationModal
        show={!!save.summary}
        title="Apply these vehicle changes?"
        description="These take effect straight away, on this vehicle's future assignments."
        confirmLabel="Update Vehicle"
        busyLabel="Saving..."
        isBusy={save.isSaving}
        error={save.error}
        onCancel={save.cancel}
        onConfirm={save.confirm}
      >
        <ChangeList changes={save.summary || []} />
      </ConfirmationModal>

      <SuccessModal
        show={!!save.saved}
        title="Vehicle updated"
        description="These are live now, on this vehicle's future assignments."
        onClose={save.dismiss}
      >
        <ChangeList changes={save.saved || []} />
      </SuccessModal>
    </AdminPage>
  );
};

export default withRouter(UpdateVehicle);
